import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { Investor, InvestorMetrics } from '../types/User';
import { investorService } from '../services/userService';
import { liquidacionService } from '../services/liquidacionService';
import InvestorNotes from '../components/investors/InvestorNotes';
import InvestorInvestments from '../components/investors/InvestorInvestments';
import ActionButton from '../components/common/ActionButton';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { FaEdit, FaArrowLeft, FaFileDownload } from 'react-icons/fa';
import { formatCurrency } from '../utils/format';
import './InvestorDetailPage.css';

const InvestorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [metrics, setMetrics] = useState<InvestorMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  // Informe trimestral
  const currentDate = new Date();
  const currentQuarter = Math.ceil((currentDate.getMonth() + 1) / 3);
  const defaultQuarter = currentQuarter === 1 ? 4 : currentQuarter - 1;
  const defaultYear = currentQuarter === 1 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
  const [reportQuarter, setReportQuarter] = useState(defaultQuarter);
  const [reportYear, setReportYear] = useState(defaultYear);
  const [downloadingReport, setDownloadingReport] = useState(false);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    if (!id) return;

    setLoading(true);
    try {
      const [investorResponse, metricsResponse] = await Promise.all([
        investorService.getById(id),
        investorService.getMetrics(id),
      ]);
      setInvestor(investorResponse.data);
      setMetrics(metricsResponse.data);
    } catch (error) {
      console.error('Error loading investor data:', error);
      showToast('Error al cargar los datos del inversor', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (investor) {
      navigate('/usuarios', { state: { editInvestor: investor } });
    }
  };

  const handleBack = () => {
    navigate('/usuarios');
  };

  const handleDownloadReport = async () => {
    if (!id) return;
    setDownloadingReport(true);
    try {
      const response = await liquidacionService.downloadReport(id, reportYear, reportQuarter);
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `Liquidacion_${reportQuarter}T${reportYear}.pdf`;
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error: any) {
      console.error('Error descargando informe:', error);
      if (error?.response?.status === 404) {
        showToast('Este inversor no tiene importes a liquidar en ese trimestre', 'info');
      } else {
        showToast('Error al generar el informe trimestral', 'error');
      }
    } finally {
      setDownloadingReport(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  if (loading) {
    return (
      <div className="investor-detail-page">
        <LoadingSpinner message="Cargando datos del inversor..." />
      </div>
    );
  }

  if (!investor) {
    return (
      <div className="investor-detail-page">
        <div className="error">No se encontró el inversor</div>
      </div>
    );
  }

  return (
    <div className="investor-detail-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <ActionButton
            icon={FaArrowLeft}
            text="Volver"
            onClick={handleBack}
            variant="detail"
          />
          <h2>{investor.nombre}</h2>
        </div>
        <div className="header-actions">
          <div className="report-controls">
            <select
              className="report-select"
              value={reportQuarter}
              onChange={(e) => setReportQuarter(Number(e.target.value))}
            >
              <option value={1}>1T</option>
              <option value={2}>2T</option>
              <option value={3}>3T</option>
              <option value={4}>4T</option>
            </select>
            <select
              className="report-select"
              value={reportYear}
              onChange={(e) => setReportYear(Number(e.target.value))}
            >
              {Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <ActionButton
              icon={FaFileDownload}
              text="Informe Trimestral"
              onClick={handleDownloadReport}
              variant="primary"
              disabled={downloadingReport}
            />
          </div>
          <ActionButton
            icon={FaEdit}
            text="Editar Inversor"
            onClick={handleEdit}
            variant="edit"
          />
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Información Básica */}
        <section className="detail-section">
          <h3>Información Básica</h3>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">NIF:</span>
              <span className="info-value">{investor.nif}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Email:</span>
              <span className="info-value">{investor.email}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Teléfono:</span>
              <span className="info-value">{investor.telefono || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Dirección:</span>
              <span className="info-value">{investor.direccion || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Estado:</span>
              <span className={`status-badge status-${investor.estado?.toLowerCase()}`}>
                {investor.estado || '-'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Procedencia:</span>
              <span className="info-value">{investor.procedencia || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Responsable Comercial:</span>
              <span className="info-value">{investor.responsableComercialNombre || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Fecha Nacimiento:</span>
              <span className="info-value">{formatDate(investor.fechaNacimiento)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Profesión:</span>
              <span className="info-value">{investor.profesion || '-'}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Cargo Público:</span>
              <span className="info-value">{investor.cargoPublico ? 'Sí' : 'No'}</span>
            </div>
          </div>
        </section>

        {/* Métricas Financieras */}
        <section className="detail-section">
          <h3>Métricas Financieras</h3>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-label">Total Invertido</div>
              <div className="metric-value">
                {metrics ? formatCurrency(metrics.totalInvertido) : '-'}
              </div>
              <div className="metric-description">
                Suma de inversiones ACTIVO + LIQUIDADO
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Total Invertido Activo</div>
              <div className="metric-value primary">
                {metrics ? formatCurrency(metrics.totalInvertidoActivo) : '-'}
              </div>
              <div className="metric-description">
                Suma de inversiones en ACTIVO
              </div>
            </div>
          </div>
        </section>

        {/* Histórico de Notas */}
        <section className="detail-section notes-section">
          <InvestorNotes investorId={id!} />
        </section>

        {/* Registro de Inversiones */}
        <section className="detail-section">
          <InvestorInvestments investorId={id!} />
        </section>
      </div>
    </div>
  );
};

export default InvestorDetailPage;
